/**
 * Solare Product Template - JavaScript
 *
 * Handles:
 * - Hero video play/pause and mute controls
 * - Scroll-down indicator smooth scroll
 * - GSAP scroll-triggered section animations
 * - Ingredients horizontal auto-scroll carousel (Swiper)
 */

import Swiper from "swiper";
import { Autoplay, FreeMode } from "swiper/modules";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

document.addEventListener("DOMContentLoaded", () => {
  initVideoControls();
  initScrollIndicator();
  initScrollAnimations();
  initIngredientsCarousel();
  initEnjoyParallax();
  initFeaturesDecoParallax();
  initLanguageSelectors();
  initHeaderScroll();
});

/* ──────────────────────────────────────────────
 * 1. Hero Video Controls
 * ────────────────────────────────────────────── */
function initVideoControls() {
  const video = document.querySelector(".solare-hero__video");
  if (!video) return;

  const playBtn = document.querySelector("[data-solare-playpause]");
  const muteBtn = document.querySelector("[data-solare-mute]");

  if (playBtn) {
    playBtn.addEventListener("click", () => {
      if (video.paused) {
        video.play();
        playBtn.classList.remove("is-paused");
        playBtn.classList.add("is-playing");
      } else {
        video.pause();
        playBtn.classList.remove("is-playing");
        playBtn.classList.add("is-paused");
      }
    });
  }

  if (muteBtn) {
    muteBtn.addEventListener("click", () => {
      video.muted = !video.muted;
      muteBtn.classList.toggle("is-muted", video.muted);
      muteBtn.classList.toggle("is-unmuted", !video.muted);
    });
  }
}

/* ──────────────────────────────────────────────
 * 2. Scroll-down Indicator
 * ────────────────────────────────────────────── */
function initScrollIndicator() {
  const scrollBtn = document.querySelector("[data-solare-scroll-down]");
  if (!scrollBtn) return;

  scrollBtn.addEventListener("click", (e) => {
    e.preventDefault();
    const nextSection = document.querySelector(".solare-enjoy");
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: "smooth" });
    }
  });
}

/* ──────────────────────────────────────────────
 * 3. GSAP Scroll Animations
 * ────────────────────────────────────────────── */
function initScrollAnimations() {
  const sections = document.querySelectorAll("[data-solare-animate]");

  sections.forEach((section) => {
    // Step items get individual triggers so each one animates as it enters view
    const stepItems = section.querySelectorAll(".solare-step-item");
    stepItems.forEach((item) => {
      gsap.fromTo(
        item,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: item,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );
    });

    // All other animatable elements use the section as trigger
    const elements = section.querySelectorAll(
      ".solare-headline, .solare-subheadline, .solare-body-text, .solare-bottle-img"
    );

    if (elements.length === 0) return;

    const isFillGlass = section.dataset.solareSection === "fill-glass";

    gsap.fromTo(
      elements,
      { y: 40, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.15,
        ease: "power2.out",
        scrollTrigger: {
          trigger: section,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: isFillGlass ? "play none none none" : "play none none reverse",
        },
      }
    );
  });
}

/* ──────────────────────────────────────────────
 * 4. Enjoy Section — Scroll Parallax (Y + scale)
 * ────────────────────────────────────────────── */
function initEnjoyParallax() {
  const section = document.querySelector(".solare-enjoy");
  if (!section) return;

  const bottle = section.querySelector(".solare-enjoy__bottle-img");
  if (!bottle) return;

  gsap.fromTo(
    bottle,
    { y: 40, scale: 0.92 },
    {
      y: -40,
      scale: 1.1,
      ease: "none",
      scrollTrigger: {
        trigger: section,
        start: "top bottom",
        end: "bottom top",
        scrub: true,
      },
    }
  );
}

/* ──────────────────────────────────────────────
 * 5. Features Deco Parallax
 * ────────────────────────────────────────────── */
function initFeaturesDecoParallax() {
  const section = document.querySelector(".solare-features");
  if (!section) return;

  const left = section.querySelector(".solare-features__deco--left");
  const right = section.querySelector(".solare-features__deco--right");

  if (left) {
    gsap.fromTo(
      left,
      { y: 150, opacity: 0 },
      {
        y: -150,
        opacity: 0.2,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      }
    );
  }

  if (right) {
    gsap.fromTo(
      right,
      { y: -150, opacity: 0 },
      {
        y: 150,
        opacity: 0.2,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      }
    );
  }
}

/* ──────────────────────────────────────────────
 * 6. Header Scroll Shrink
 * ────────────────────────────────────────────── */
function initHeaderScroll() {
  const header = document.getElementById("solare-header");
  if (!header) return;

  const onScroll = () => {
    header.classList.toggle("is-scrolled", window.scrollY > 10);
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();
}

/* ──────────────────────────────────────────────
 * 7. Language Selector
 * ────────────────────────────────────────────── */
function initLanguageSelectors() {
  document.querySelectorAll(".language-selector").forEach((selector) => {
    selector.addEventListener("change", function () {
      window.location.href = this.value;
    });
  });
}

/* ──────────────────────────────────────────────
 * 7. Ingredients Carousel (Swiper)
 * ────────────────────────────────────────────── */
function initIngredientsCarousel() {
  const el = document.querySelector(".solare-ingredients__carousel");
  if (!el) return;

  new Swiper(el, {
    modules: [Autoplay, FreeMode],
    slidesPerView: "auto",
    spaceBetween: 16,
    loop: true,
    freeMode: {
      enabled: true,
      momentum: true,
    },
    autoplay: {
      delay: 0,
      disableOnInteraction: false,
    },
    speed: 4000,
    grabCursor: true,
  });
}
